import { FieldParser, ValidationWarning } from './types';

const writeDrift = (warnings: Map<string, ValidationWarning>, path: string, expected: string, received: unknown, usedDefault: unknown): void => {
  warnings.set(path, { path, expected, received, usedDefault });
};

export const str =
  (defaultValue = ''): FieldParser<string> =>
  (raw, path, warnings) => {
    if (typeof raw === 'string') return { ok: true, value: raw };
    writeDrift(warnings, path, 'string', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

export const num =
  (defaultValue = 0): FieldParser<number> =>
  (raw, path, warnings) => {
    if (typeof raw === 'number' && !Number.isNaN(raw)) {
      return { ok: true, value: raw };
    }
    writeDrift(warnings, path, 'number', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

export const bool =
  (defaultValue = false): FieldParser<boolean> =>
  (raw, path, warnings) => {
    if (typeof raw === 'boolean') return { ok: true, value: raw };
    writeDrift(warnings, path, 'boolean', raw, defaultValue);
    return { ok: true, value: defaultValue };
  };

/**
 * Accepts any value (including null and undefined) and returns it as
 * `unknown`. No defaulting, no warning — the wire shape is unknown by
 * design (e.g. opaque JSON payloads, loosely-typed config fields). The
 * caller is expected to narrow the result before using it.
 *
 * Wrap with `nullable` if a missing key should distinguish from an
 * intentional `null`.
 */
export const unknownValue = (): FieldParser<unknown> => (raw) => ({
  ok: true,
  value: raw,
});

/**
 * Like `unknownValue`, but guarantees the result is dereferenceable: `raw` must
 * be a plain object (non-null, non-array); its contents pass through verbatim as
 * `Record<string, unknown>` (opaque by design — dynamic JSON the caller
 * re-parses). A non-object (including `null`) fails (`ok: false`), so a consumer
 * that dereferences the value can't be handed a `null` that type-checks as an
 * object. Wrap with `withDefault(unknownRecord(), {})` for a scalar field, or use
 * `array(unknownRecord())` to drop malformed elements rather than pass them on.
 */
export const unknownRecord = (): FieldParser<Record<string, unknown>> => (raw, path, warnings) => {
  if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) {
    return { ok: true, value: raw as Record<string, unknown> };
  }
  writeDrift(warnings, path, 'object', raw, undefined);
  return { ok: false };
};

/**
 * Validates that `raw` is strictly equal (`===`) to one of `values`. Used
 * for string- and number-literal unions (enums). On mismatch, returns the
 * supplied `defaultValue` with a warning.
 *
 * Both `values` and `defaultValue` are linked to the same type parameter
 * `T`, which the `const` modifier infers as a literal tuple. As a result:
 *   - `oneOf(['a', 'b'], 'a')` returns `FieldParser<'a' | 'b'>` (narrowed).
 *   - `oneOf<['a', 'b']>([], 'a')` is a compile error (`[]` doesn't
 *     satisfy the tuple).
 *   - `oneOf(['a', 'b'], 'c')` is a compile error (`'c'` is not in T).
 */
export const oneOf =
  <const T extends readonly (string | number)[]>(values: T, defaultValue: T[number]): FieldParser<T[number]> =>
  (raw, path, warnings) => {
    if ((values as readonly unknown[]).includes(raw)) {
      return { ok: true, value: raw as T[number] };
    }
    const expected = `one of ${values.map((v) => JSON.stringify(v)).join(', ')}`;
    writeDrift(warnings, path, expected, raw, defaultValue);
    return { ok: true, value: defaultValue };
  };
